<script setup lang="ts">
import { computed, reactive, ref, watch } from 'vue';
import { useHead } from '@unhead/vue';
import { Tab, TabGroup, TabList, TabPanel, TabPanels } from '@headlessui/vue';
import { onBeforeRouteUpdate, useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { refDebounced } from '@vueuse/core';
import type {
  Collection,
  GetProfileResponse,
  GetUserStatsResponse,
  UserBadge,
} from '~/utilities/pyscript-api-models';
import { show404Page } from '~/utilities/show-404-page';
import pyscriptApi from '~/utilities/pyscript-api';
import UserProfilePicture from '~/components/UserProfilePicture.vue';
import AppHeader from '~/components/AppHeader.vue';
import SearchProjects from '~/components/SearchProjects.vue';
import SearchProjectsCollapsible from '~/components/SearchProjectsCollapsible.vue';
import UserProfile from '~/components/UserProfile.vue';
import UserBadges from '~/components/UserBadges.vue';
import UserStats from '~/components/UserStats.vue';
import Spinner from '~/components/Spinner.vue';
import ProjectCatalog from '~/components/project-catalog/ProjectCatalog.vue';
import ProjectSettingsModal from '~/components/project-settings/ProjectSettingsModal.vue';
import EditUserProfile from '~/components/account-settings/EditAccountProfile.vue';
import SortDropdown from '~/components/SortDropdown.vue';
import ListGridToggle from '~/components/ListGridToggle.vue';
import ItemsPerPageDropdown from '~/components/ItemsPerPageDropdown.vue';
import CollectionCatalog from '~/components/collections/CollectionCatalog.vue';
import EmptySearchResults from '~/components/EmptySearchResults.vue';
import EmptyNoneCreated from '~/components/EmptyNoneCreated.vue';
import NewProjectButton from '~/components/buttons/NewProjectButton.vue';
import NewCollectionButton from '~/components/buttons/NewCollectionButton.vue';
import { useProjectSettingsModalStore } from '~/stores/project-settings-modal-store';
import { useUserStore } from '~/stores/user-store';
import { useCollectionsStore } from '~/stores/collections-store';
import { usePaginatedProjectList } from '~/composables/paginated-project-list';
import { useSortPreference } from '~/composables/sort-preference';

// Populated by the Vue Router
const props = defineProps<{
  usernameOrUserId: string;
}>();

const { t } = useI18n();
const router = useRouter();
const route = useRoute();
const userStore = useUserStore();
const collectionsStore = useCollectionsStore();
const isEditingProfile = ref(false);
const projectSettingsModalStore = useProjectSettingsModalStore();
const { savedSort } = useSortPreference();

const tabTitleList = [
  { title: t('profile.projects_title'), query: 'projects' },
  { title: t('profile.collections_title'), query: 'collections' },
];

const state = reactive({
  loading: true,
  profile: null as GetProfileResponse | null,
  badges: [] as UserBadge[],
  stats: {} as GetUserStatsResponse,
  /** Only used if not the profile owner. If they are the owner the collectionsStore is used. */
  nonOwnerCollections: [] as Collection[],
  isSearchOpen: false,
  tabTitles: tabTitleList.map((tab) => tab.title),
  activeTab: 0,
});

const isOwner = computed(() => {
  if (!userStore.isLoggedIn) return false;
  return userStore.user?.id === state.profile?.id;
});

const searchQuery = ref('');
const collectionsSearchQueryDebounced = refDebounced(searchQuery, 300);
const collectionsSearchResults = ref<Collection[]>([]);

/**
 * Determines which list of collections is visible. If the user is searching,
 */
const collectionsList = computed(() => {
  if (collectionsSearchQueryDebounced.value) {
    return collectionsSearchResults.value;
  }

  return isOwner.value ? collectionsStore.collections : state.nonOwnerCollections;
});

if (router.currentRoute.value.query.q) {
  searchQuery.value = router.currentRoute.value.query.q as string;
  onSearch();
}

// Fetch collections search results when the debounced search value changes.
watch(collectionsSearchQueryDebounced, async (query) => {
  if (query) {
    if (isOwner.value) {
      collectionsSearchResults.value = await collectionsStore.fetchSearchResults(
        collectionsSearchQueryDebounced.value,
      );
    } else {
      collectionsSearchResults.value = await pyscriptApi.listCollections({
        q: collectionsSearchQueryDebounced.value,
        user: state.profile!.id,
        sortBy: savedSort.value.sortBy,
        sortOrder: savedSort.value.sortOrder,
      });
    }
  } else {
    refreshCollections();
  }
});

// Refetch collections when the sort changes.
watch(savedSort, () => {
  refreshCollections();
});

const searchPlaceholder = computed(() => {
  return state.activeTab === 1
    ? t('general.search_collections_placeholder')
    : t('general.search_placeholder');
});

const projectPagination = reactive(
  usePaginatedProjectList({
    scope: 'other',
    user: computed(() => props.usernameOrUserId),
    q: searchQuery,
  }),
);

async function pageTitle() {
  if (!state.profile) return '';
  const { username, name, id } = state.profile;
  return username && name ? `@${username} (${name})` : username || name || id;
}

useHead({ title: pageTitle() });

const avatar = computed(() => state.profile?.avatar || '');

initData(props.usernameOrUserId);

/**
 * When the route changes, fetch the new data.
 *
 * For example, if I was viewing someone else's profile and then navigated to
 * my own profile, this is considered a route update.
 */
onBeforeRouteUpdate(async (to, from) => {
  /**
   * The paths would be the same in the case where we add the q query param
   * when filtering projects using the search field.
   */
  if (to.path !== from.path) {
    await initData(to.params.usernameOrUserId as string);
  }
});

/*
 * * * * * * * * * * * *
 *       METHODS       *
 * * * * * * * * * * * *
 */

async function initData(usernameOrUserId: string) {
  state.loading = true;

  // Update the active tab based on the query params.
  if (route.query.tab) {
    const tabIndex = tabTitleList.findIndex((tab) => tab.query === route.query.tab);

    if (tabIndex !== -1) {
      state.activeTab = tabIndex;
    }
  }

  const [profileResult, badgesResult, statsResult] = await Promise.allSettled([
    pyscriptApi.getProfile(usernameOrUserId),
    pyscriptApi.listUserBadges(usernameOrUserId),
    pyscriptApi.userStats(usernameOrUserId),
  ]);

  if (profileResult.status === 'fulfilled') {
    state.profile = profileResult.value;

    if (!isOwner.value) {
      state.nonOwnerCollections = isOwner.value
        ? collectionsStore.collections
        : await pyscriptApi.listCollections({ user: state.profile!.id });
    }
  } else {
    show404Page();
    return;
  }

  if (statsResult.status === 'fulfilled') {
    state.stats = statsResult.value;
  }

  if (badgesResult.status === 'fulfilled') {
    state.badges = badgesResult.value;
  }

  state.loading = false;
}

function changeTab(index: number) {
  router.replace({ query: { tab: index === 0 ? undefined : tabTitleList[index].query } });
  state.activeTab = index;
}

async function refreshProjectList() {
  await projectPagination.refetch();
}

async function refreshCollections() {
  if (isOwner.value) {
    return await collectionsStore.fetchCollections();
  }

  state.nonOwnerCollections = await pyscriptApi.listCollections({
    user: state.profile!.id,
    sortBy: savedSort.value.sortBy,
    sortOrder: savedSort.value.sortOrder,
  });
}

function onSearch() {
  const query = searchQuery.value ? { q: searchQuery.value } : {};
  router.replace({ query });
}

function onTagClick(tag: string) {
  state.isSearchOpen = true;
  searchQuery.value = searchQuery.value === tag ? '' : tag;
  onSearch();
}

async function refetchUserProfile(id: string) {
  isEditingProfile.value = false;
  const profile = await pyscriptApi.getProfile(id);

  if (profile) {
    state.profile = profile;
  }
}
</script>

<template>
  <ProjectSettingsModal
    v-if="projectSettingsModalStore.project"
    @project-deleted="
      () => {
        projectSettingsModalStore.close();
        refreshProjectList();
      }
    "
    @project-updated="refreshProjectList"
  />

  <div
    class="container-spacing border-b border-new-gray-100 bg-white dark:border-new-gray-950/80 dark:bg-new-slate-700"
  >
    <AppHeader class="flex items-center !gap-0">
      <template #leftColumn>
        <div class="relative flex w-full items-center">
          <h1 class="text-lg font-semibold">{{ $t('profile.heading') }}</h1>

          <!-- Visible on mobile only. -->
          <div class="ml-auto flex h-8 items-center md:hidden">
            <SearchProjectsCollapsible
              v-model="searchQuery"
              v-model:is-open="state.isSearchOpen"
              :search-fn="onSearch"
              :placeholder="searchPlaceholder"
            />
          </div>
        </div>
      </template>

      <template #centerColumn>
        <!-- Visible on desktop only. -->
        <SearchProjects
          v-model="searchQuery"
          class="hidden md:block md:w-80"
          :search-fn="onSearch"
          :placeholder="searchPlaceholder"
        />
      </template>
    </AppHeader>
  </div>

  <div v-if="state.loading" class="flex h-full items-center justify-center text-gray-600">
    <Spinner class="h-8 w-8" />
  </div>

  <main v-else class="container-spacing overflow-auto pt-3 md:flex md:flex-auto md:gap-x-12">
    <aside
      class="custom-scrollbar w-full shrink-0 overflow-auto pb-4 md:sticky md:top-0 md:max-w-xs"
    >
      <EditUserProfile
        v-if="isOwner && state.profile && isEditingProfile"
        :profile="state.profile"
        :cancel-btn-handler="() => (isEditingProfile = false)"
        @profile-updated="refetchUserProfile(usernameOrUserId)"
      />

      <div v-show="!isEditingProfile" class="flex flex-col gap-5">
        <section v-if="state.profile" class="flex flex-row flex-wrap items-center gap-3">
          <div class="relative mt-2 h-20 w-20 flex-shrink-0 rounded-full">
            <UserProfilePicture :avatar="avatar" />
          </div>

          <div>
            <h2 class="text-blue-700 text-xl font-semibold leading-none outline-none">
              {{ state.profile.name }}
            </h2>

            <p>{{ `@${state.profile.username || usernameOrUserId}` }}</p>
          </div>
        </section>

        <button
          v-if="isOwner"
          class="btn --space-sm --primary max-w-[105px] px-4 text-sm font-medium"
          @click="isEditingProfile = true"
        >
          Edit Profile
        </button>

        <UserProfile :username-or-id="usernameOrUserId" :user="state.profile" :is-owner="isOwner" />

        <hr class="border-t border-gray-200 dark:border-new-gray-950" />

        <UserStats :stats="state.stats" class="text-sm" />

        <UserBadges v-if="state.badges.length" :user-badges="state.badges" class="max-w-md" />
      </div>
    </aside>

    <section v-if="projectPagination.projectList !== null" class="h-min w-full min-w-0 pb-5">
      <TabGroup :selected-index="state.activeTab" @change="changeTab">
        <div class="mb-2 flex items-center">
          <TabList class="flex flex-nowrap gap-6">
            <Tab v-for="title of state.tabTitles" :key="title" v-slot="{ selected }" as="template">
              <button
                class="py-2 text-base font-semibold leading-none outline-none"
                :class="[
                  selected
                    ? 'border-b-2 border-b-new-green-500'
                    : 'border-b-2 border-b-transparent text-gray-500 hover:text-gray-600 dark:text-gray-300',
                ]"
              >
                {{ title }}
              </button>
            </Tab>
          </TabList>

          <div class="ml-auto flex gap-1">
            <ListGridToggle class="ml-1" />
            <SortDropdown class="ml-1" />
            <ItemsPerPageDropdown class="ml-1.5" />
          </div>
        </div>

        <TabPanels>
          <!-- Projects -->
          <TabPanel :unmount="false">
            <!-- No Projects -->
            <template v-if="projectPagination.hasNoProjects">
              <EmptyNoneCreated v-if="isOwner" class="mt-10" i18n-key="states.noun.projects">
                <template #button>
                  <NewProjectButton />
                </template>
              </EmptyNoneCreated>

              <EmptyNoneCreated
                v-else
                class="mt-10"
                i18n-key="states.noun.projects"
                :description="$t('profile.no_projects.non_owner_text')"
              />
            </template>

            <!-- No search results -->
            <EmptySearchResults
              v-else-if="searchQuery && projectPagination.projectList?.length === 0"
              i18n-key="states.noun.projects"
            />

            <!-- Catalog -->
            <ProjectCatalog
              v-else-if="projectPagination.projectList && projectPagination.pagination"
              :project-list="projectPagination.projectList"
              :pagination="projectPagination.pagination"
              :set-page="projectPagination.setPage"
              :tag-click-handler="onTagClick"
              :refetch="refreshProjectList"
            />
          </TabPanel>

          <!-- Collections -->
          <TabPanel>
            <template v-if="collectionsList.length === 0">
              <!-- No search results -->
              <EmptySearchResults
                v-if="collectionsSearchQueryDebounced"
                i18n-key="states.noun.collections"
              />

              <!-- No Collections -->
              <EmptyNoneCreated
                v-else
                class="mt-10"
                i18n-key="states.noun.collections"
                :description="isOwner ? undefined : $t('profile.no_collections.non_owner_text')"
              >
                <template v-if="isOwner" #button>
                  <NewCollectionButton />
                </template>
              </EmptyNoneCreated>
            </template>

            <CollectionCatalog v-else :collections="collectionsList" />
          </TabPanel>
        </TabPanels>
      </TabGroup>
    </section>
  </main>
</template>

<style scoped lang="postcss">
.slide-fade-enter-active,
.slide-fade-leave-active {
  transition: all 0.3s;
}
.slide-fade-enter,
.slide-fade-leave-to {
  opacity: 0;
  transform: scale(0.5);
}
.slide-fade-leave-active {
  position: absolute;
}
</style>
